import { useState } from 'react';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const INITIAL = { name: '', phone: '', email: '', interest: 'cumpleanos', event_date: '', message: '', preferred_channel: 'whatsapp' };

export const EventLeadForm = ({ source = 'event_form', defaultInterest = 'cumpleanos' }) => {
    const [form, setForm] = useState({ ...INITIAL, interest: defaultInterest });
    const [submitting, setSubmitting] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !(form.phone || form.email)) {
            toast.error('Por favor completa tu nombre y al menos un dato de contacto.');
            return;
        }
        setSubmitting(true);
        try {
            await publicApi.submitLead({ ...form, source });
            trackEvent('form_submit', window.location.pathname, { source });
            toast.success('¡Gracias! Pronto te contactaremos.');
            setForm({ ...INITIAL, interest: defaultInterest });
        } catch {
            toast.error('No pudimos enviar tu solicitud. Inténtalo nuevamente.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form data-testid="event-lead-form" onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label htmlFor="el-name">Nombre completo *</Label><Input id="el-name" data-testid="event-lead-name" value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-1" placeholder="Tu nombre" /></div>
            <div><Label htmlFor="el-phone">Teléfono / WhatsApp</Label><Input id="el-phone" data-testid="event-lead-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="mt-1" placeholder="+593 ..." /></div>
            <div><Label htmlFor="el-email">Email</Label><Input id="el-email" data-testid="event-lead-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1" placeholder="correo@ejemplo.com" /></div>
            <div><Label htmlFor="el-type">Tipo de evento</Label>
                <Select value={form.interest} onValueChange={(v) => set('interest', v)}>
                    <SelectTrigger id="el-type" data-testid="event-lead-interest" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="cumpleanos">Cumpleaños</SelectItem>
                        <SelectItem value="eventos">Evento corporativo / escolar</SelectItem>
                        <SelectItem value="grupo">Grupo / amigos</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div><Label htmlFor="el-date">Fecha estimada</Label><Input id="el-date" data-testid="event-lead-date" type="date" value={form.event_date} onChange={(e) => set('event_date', e.target.value)} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label htmlFor="el-msg">Mensaje</Label><Textarea id="el-msg" data-testid="event-lead-message" rows={3} value={form.message} onChange={(e) => set('message', e.target.value)} className="mt-1" placeholder="Cuéntanos brevemente sobre tu evento (número de invitados, edades, ideas...)" /></div>
            <div className="sm:col-span-2">
                <Label>Canal preferido</Label>
                <div className="mt-1 flex flex-wrap gap-3 text-sm">
                    {[['whatsapp', 'WhatsApp'], ['phone', 'Llamada'], ['email', 'Email']].map(([k, l]) => (
                        <label key={k} className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-border px-3 py-1.5 has-[:checked]:bg-[hsl(var(--kche-pink))] has-[:checked]:text-white has-[:checked]:border-[hsl(var(--kche-pink))] transition">
                            <input type="radio" name="channel" value={k} checked={form.preferred_channel === k} onChange={(e) => set('preferred_channel', e.target.value)} className="sr-only" />
                            {l}
                        </label>
                    ))}
                </div>
            </div>
            <div className="sm:col-span-2">
                <button type="submit" disabled={submitting} data-testid="event-lead-submit" className="inline-flex items-center gap-2 rounded-full bg-gradient-fun text-white font-bold font-display px-6 py-3 text-sm shadow-pop hover:scale-[1.02] disabled:opacity-60 transition">
                    <Send className="size-4" /> {submitting ? 'Enviando…' : 'Solicitar información'}
                </button>
            </div>
        </form>
    );
};

export default EventLeadForm;
